import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Pill } from "@/pages/Tickets";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { GitBranch, ArrowLeft, MapPin, Clock, DoorOpen, Users as UsersIcon, ClipboardCheck, LifeBuoy } from "lucide-react";

const SLA = { overdue: "text-rose-600", due_soon: "text-amber-600", on_track: "text-emerald-600", met: "text-slate-400" };

export default function BranchDetail() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get(`/branches/${bid}`).then((r) => setBranch(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/rooms?branch_id=${bid}`).then((r) => setRooms(r.data)).catch(() => {});
    api.get(`/visitors?branch_id=${bid}`).then((r) => setVisitors(r.data.slice(0, 100))).catch(() => {});
    api.get(`/users`).then((r) => setUsers(r.data.filter((u) => u.branch_id === bid))).catch(() => {});
    api.get(`/tickets?branch_id=${bid}`).then((r) => setTickets(r.data.slice(0, 200))).catch(() => {});
  }, [bid]);

  if (!branch) return <Loader />;

  return (
    <div>
      <button onClick={() => navigate(-1)} data-testid="back-from-branch" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 mb-4 transition-colors">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 sf-fade-up">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><GitBranch size={26} /></div>
            <div>
              <h1 className="font-display text-2xl font-bold text-navy-900">{branch.name}</h1>
              <p className="text-sm text-slate-400 font-mono">{branch.code} · {branch.company_name}</p>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-3">
                <span className="flex items-center gap-1"><MapPin size={13} /> {branch.address || "—"}</span>
                <span className="flex items-center gap-1"><Clock size={13} /> {branch.working_hours}</span>
              </p>
            </div>
          </div>
          <StatusBadge status={branch.status} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat icon={DoorOpen} label="Meeting Rooms" value={branch.room_count} />
          <Stat icon={ClipboardCheck} label="Visitors" value={branch.visitor_count} />
          <Stat icon={UsersIcon} label="Users" value={branch.user_count} />
          <Stat icon={LifeBuoy} label="Service Requests" value={branch.ticket_count}
            sub={branch.ticket_open ? `${branch.ticket_open} open${branch.ticket_overdue ? ` · ${branch.ticket_overdue} overdue` : ""}` : null}
            subTone={branch.ticket_overdue ? "text-rose-600" : "text-amber-600"} />
        </div>
      </div>

      <Tabs defaultValue="tickets" className="mt-6">
        <TabsList className="mb-4">
          <TabsTrigger value="tickets" data-testid="branch-tab-tickets">Service Requests</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="branch-tab-rooms">Meeting Rooms</TabsTrigger>
          <TabsTrigger value="visitors" data-testid="branch-tab-visitors">Visitors</TabsTrigger>
          <TabsTrigger value="users" data-testid="branch-tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets">
          {tickets.length === 0 ? <EmptyState icon={LifeBuoy} title="No service requests" subtitle="Tickets raised from this branch's cabins will appear here." /> : (
            <Panel>
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Ticket</TableHead><TableHead>Issue</TableHead><TableHead>Cabin</TableHead><TableHead>Priority</TableHead><TableHead>Dept</TableHead><TableHead>Assigned</TableHead><TableHead>SLA</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {tickets.map((t) => (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/tickets/${t.id}`)} data-testid={`branch-ticket-row-${t.ticket_number}`}>
                      <TableCell className="font-mono text-xs font-semibold text-navy-900">{t.ticket_number}</TableCell>
                      <TableCell className="text-navy-800">{t.issue}<div className="text-xs text-slate-400">{t.category}</div></TableCell>
                      <TableCell className="text-slate-500 text-sm">{t.requester?.cabin || "—"}</TableCell>
                      <TableCell className="text-sm">{t.priority}</TableCell>
                      <TableCell className="text-sm text-slate-500">{t.department}</TableCell>
                      <TableCell className="text-sm text-slate-500">{t.assigned_user_name || <span className="text-indigo-500">Unassigned</span>}</TableCell>
                      <TableCell className={`text-xs font-semibold ${SLA[t.sla?.state]}`}>{t.sla?.label}</TableCell>
                      <TableCell><Pill s={t.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="rooms">
          {rooms.length === 0 ? <EmptyState icon={DoorOpen} title="No meeting rooms" /> : (
            <Panel>
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Capacity</TableHead><TableHead>Facilities</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {rooms.map((r) => (
                    <TableRow key={r.id} data-testid={`branch-room-row-${r.code}`}>
                      <TableCell className="font-medium text-navy-900">{r.name}</TableCell>
                      <TableCell className="text-slate-500 font-mono text-xs">{r.code}</TableCell>
                      <TableCell className="text-slate-500">{r.capacity}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{(r.facilities || []).join(", ") || "—"}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="visitors">
          {visitors.length === 0 ? <EmptyState icon={ClipboardCheck} title="No visitors" /> : (
            <Panel>
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Visitor</TableHead><TableHead>Company</TableHead><TableHead>Host</TableHead><TableHead>Cabin</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {visitors.map((v) => (
                    <TableRow key={v.id} data-testid={`branch-visitor-row-${v.id}`}>
                      <TableCell className="font-medium text-navy-900">{v.visitor_name}</TableCell>
                      <TableCell className="text-slate-500">{v.visitor_company}</TableCell>
                      <TableCell className="text-slate-500">{v.person_to_meet}</TableCell>
                      <TableCell className="text-slate-500">{v.cabin_number || "—"}</TableCell>
                      <TableCell><StatusBadge status={v.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="users">
          {users.length === 0 ? <EmptyState icon={UsersIcon} title="No users" subtitle="No branch admins or receptionists created for this branch yet." /> : (
            <Panel>
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`branch-user-row-${u.email}`}>
                      <TableCell className="font-medium text-navy-900">{u.name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell><span className="text-xs font-semibold text-navy-800 bg-navy-50 px-2 py-1 rounded-md">{u.role.replace(/_/g, " ")}</span></TableCell>
                      <TableCell><StatusBadge status={u.active ? "active" : "inactive"} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, subTone }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
      <span className="w-9 h-9 rounded-lg bg-white text-navy-800 flex items-center justify-center border border-slate-200"><Icon size={17} /></span>
      <div>
        <p className="text-lg font-bold text-navy-900 leading-none">{value ?? 0}</p>
        <p className="text-xs text-slate-400 mt-0.5">{label}</p>
        {sub && <p className={`text-[11px] font-semibold mt-0.5 ${subTone || "text-slate-400"}`}>{sub}</p>}
      </div>
    </div>
  );
}

function Panel({ children }) {
  return <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden sf-fade-up">{children}</div>;
}
